import { MarginedBoundingBox } from "../../../../Types/MarginedBoundingBox"
import { Offset } from "../../../../Types/Offset"
import { ModalityGraphConfig } from "../../../../Types/Graph"
import { D3TimeBasedVisualization } from "../../D3TimeBasedVisualization"
import { D3ModalityGraphGroupConfigurationBuilder } from "./D3ModalityGraphGroupConfigurationBuilder"
import { D3ModalityGraphGroupRenderer } from "./D3ModalityGraphGroupRenderer"
import { ModalityGraphGroupReactCallbacks } from "../../../../Types/ReactCallbacks"
import { ModalityGraphGroupConfig } from "../../../../Types/ModalityGraphGroup"
import { TimeSeriesPageManager } from "../../../../Data/TimeSeriesPageManager"
import { ModalityPage } from "../../../../Data/ModalityPage"
import { LinkedWindowJumpEvent } from "../../../../Types/LinkedWindowInfo"
import { getVisibleGraphs } from "../visibleGraphs"
import { LEFT_MARGIN, MULTI_GRAPH_SPACING, NUMERIC_VALUE_MARGIN, RIGHT_MARGIN, TIMELINE_SPACING } from "../../Constants"
import { ModalityDataSource } from "../../../../Types/ModalityDataSource"
import { getQEEGDSARequestAnalysis } from "../../../../Types/AnalysisDetails"
import { getFullTraceDataConfigs, isQEEGDSAAnalysis, RenderStrategy, TraceDataConfig } from "../../../../Types/Trace"
import { Page } from "../../../../Data/Page"

export class D3ModalityGraphGroup extends D3TimeBasedVisualization<
	ModalityGraphGroupConfig,
	ModalityGraphGroupReactCallbacks,
	D3ModalityGraphGroupRenderer,
	TimeSeriesPageManager<ModalityPage>
> {
	public graphsBoundingBox: MarginedBoundingBox
	public graphsMargins: Offset = { top: 25, left: LEFT_MARGIN, bottom: 60 + TIMELINE_SPACING, right: NUMERIC_VALUE_MARGIN }
	public overlayMargins: Offset = { top: MULTI_GRAPH_SPACING, bottom: MULTI_GRAPH_SPACING, left: 0, right: 0 }
	public overlayBoundingBox: MarginedBoundingBox
	public graphs: ModalityGraphConfig[] = []
	public startTime: number | undefined = undefined
	public endTime: number | undefined = undefined

	constructor(root: HTMLDivElement, config: ModalityGraphGroupConfig, pageManager: TimeSeriesPageManager<any>, reactCallbacks: ModalityGraphGroupReactCallbacks) {
		super(root, config, pageManager, reactCallbacks)
		this.graphsBoundingBox = new MarginedBoundingBox(config.dimensions, this.graphsMargins)
		this.overlayBoundingBox = new MarginedBoundingBox({ width: this.graphsBoundingBox.width, height: this.graphsBoundingBox.height }, this.overlayMargins)
		this.mount(new D3ModalityGraphGroupRenderer(this, new D3ModalityGraphGroupConfigurationBuilder(this), "d3-modality-graph-group"))
		this.autoScale()
	}

	public clearDataAndReload = () => {
		this.timeSeriesPageManager.resetPages()
		this.renderer?.graphsWrapper?.clearSnapshot()
		this.updateConfig(this.config)
		this.timeSeriesPageManager.clearQueueAndLoad()
		this.autoScale()
	}

	protected onPageLoaded = (page: Page<any>) => {
		const modalityPage = page as ModalityPage // to make TS happy
		const pagesInView = this.timeSeriesPageManager.getPagesInView()
		this.renderer?.timeline?.updateNotLoadedRegions()

		if (pagesInView.includes(modalityPage)) {
			this.renderPage(modalityPage)
			this.updateWindowLoadingState()

			if (modalityPage.needsAutoScale) {
				this.autoScale()
			}
		}
	}

	public autoScale = () => {
		this.renderer?.autoScaleGraphs()
	}

	public getOverlay = () => this.renderer?.graphsOverlay

	public getLastHoveredDate = () => this.getOverlay()?.getLastHoveredDate()

	protected renderPage(page: ModalityPage): void {
		this.renderer?.graphsWrapper?.renderPage(page)
	}

	public viewTimesChanged = (startTime: number, endTime: number) => {
		const { viewDuration } = this.getStartTimeEndTimeViewDuration()
		const calculatedViewDuration = endTime - startTime

		if (viewDuration !== calculatedViewDuration) {
			// If the view duration has changed, we need to let React handle the update.
			// Otherwise, we end up reverting the duration back to the old duration.
			// This bug is caused by the updates from another component's timeline when it is playing.
			if (this.anotherLinkedWindowIsPlaying()) {
				return
			}
		}

		this.config.viewScale.domain([startTime, endTime])
		this.renderer?.viewTimesChanged()

		if (!this.renderer?.graphsWrapper?.isRescaling()) {
			this.timeSeriesPageManager?.clearQueueAndLoad()
		}
	}

	public onTimeAxisDragStart = () => {
		this.renderer?.graphsWrapper?.takeSnapshot()
	}

	public onTimeAxisDrag = () => {
		this.renderer?.onTimeAxisDrag()
		this.updateLinkedWindows()
	}

	public onTimelineSliderDrag = () => {
		this.timeSeriesPageManager?.clearQueueAndLoad()
		this.renderer?.onTimelineSliderDrag()
		this.updateLinkedWindows()
	}

	public onTimelineSliderDragEnd = () => {
		this.timeSeriesPageManager?.clearQueueAndLoad()
		this.updateLinkedWindows({ autoScale: true })
		this.autoScale()
	}

	public getVisibleTraces(): TraceDataConfig[] {
		return this.config.graphs?.flatMap(graph => graph.traces.filter(trace => trace.isVisible ?? true).flatMap(getFullTraceDataConfigs))
	}

	public clearCacheAndRedraw = () => {
		this.timeSeriesPageManager.getAllLoadedPages().forEach((page: ModalityPage) => page.clearRenderCache())
		this.timeSeriesPageManager.clearQueueAndLoad()
		this.autoScale()
	}

	// PROTECTED

	protected getModalityDataSources(): ModalityDataSource[] {
		return [...new Set(this.config.graphs.flatMap(graph => graph.traces))].flatMap((trace): ModalityDataSource | ModalityDataSource[] => {
			switch (trace.renderStrategy) {
				case RenderStrategy.DELTA:
					return [trace.first, trace.second].map(config => ({
							modality: config.dataKey,
							dataObjectId: this.reactCallbacks.dataSourceMap.get(config.dataSource) ?? Infinity,
							onDemandAnalysis: config.onDemandAnalysis
						}))
				case RenderStrategy.SPECTROGRAM:
					if (isQEEGDSAAnalysis(trace.onDemandAnalysis)) {
						return {
							modality: trace.dataKey,
							dataObjectId: this.reactCallbacks.dataSourceMap.get(trace.dataSource) ?? Infinity,
							onDemandAnalysis: getQEEGDSARequestAnalysis(trace.onDemandAnalysis)
						}
					}

					return []
				default:
					return {
						modality: trace.dataKey,
						dataObjectId: this.reactCallbacks.dataSourceMap.get(trace.dataSource) ?? Infinity,
						onDemandAnalysis: trace.onDemandAnalysis
					}
			}
		})
	}

	protected onLinkedWindowsUpdate = (event: LinkedWindowJumpEvent) => {
		if (!this.renderer?.graphsWrapper?.isRescaling() && this.config.isLinked && event.controller !== this.config.id) {
			this.onJumpToTime(event)

			if (event.options?.autoScale) {
				this.renderer?.autoScaleGraphs()
			}
		}
	}

	protected updateDerivedState = () => {
		this.graphsBoundingBox.setDimensions(this.config.dimensions)

		const visibleGraphs = getVisibleGraphs(this.config.graphs, this.config.patientModalities, this.config.hideEmptyGraphs)
		const anyNumericDisplays = visibleGraphs.some(graph => graph.traces.some(trace => trace.summaryValueConfig?.isVisible))

		// We change the graphs bounding box depending on if there are numeric displays or not.
		if (anyNumericDisplays) {
			this.graphsBoundingBox.setMargins({ ...this.graphsMargins, right: NUMERIC_VALUE_MARGIN })
		} else {
			this.graphsBoundingBox.setMargins({ ...this.graphsMargins, right: RIGHT_MARGIN })
		}

		// Anything that relies on the graphs bounding box must go below this line
		this.overlayBoundingBox.setDimensions({ height: this.graphsBoundingBox.height, width: this.graphsBoundingBox.width })
		this.config.viewScale.range([0, this.graphsBoundingBox.width])

		// Calculate total height units based on each graph's ratio
		const totalHeightUnits = visibleGraphs.reduce((sum, graph) => sum + (graph.heightRatio || 1), 0)

		// Calculate the available height for graphs (excluding spacing)
		const totalSpacing = MULTI_GRAPH_SPACING * (visibleGraphs.length + 1)
		const availableHeight = this.graphsBoundingBox.height - totalSpacing

		// Calculate base unit height
		const unitHeight = availableHeight / totalHeightUnits

		// Build graphs with proportional heights and accumulated offsets
		let currentOffset = MULTI_GRAPH_SPACING

		this.graphs = visibleGraphs.map((graph) => {
			const heightRatio = graph.heightRatio || 1
			const graphHeight = Math.max(unitHeight * heightRatio, 1)
			
			const graphConfig = {
				...graph,
				xScale: this.config.viewScale,
				height: graphHeight,
				width: this.graphsBoundingBox.width,
				offset: currentOffset,
			};
			
			// Update offset for next graph
			currentOffset += graphHeight + MULTI_GRAPH_SPACING
			
			return graphConfig
		});

		this.renderer?.updateChildren()
	}
}
